import {
    AuthError,
    BrowserAuthError,
    InteractionRequiredAuthError,
    type IPublicClientApplication,
    type SilentRequest,
} from "@azure/msal-browser";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";
import { loginRequest, scopesNeeded } from "./authConfig";

interface FetchParams {
    url: string;
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
    payload?: Record<string, unknown> | Record<string, unknown>[];
    instance: IPublicClientApplication;
    expectedStatus?: number;
}

type isForbiddenGetRequestInput = {
    response: Response;
    options: RequestInit;
    endpoint: string;
};

type ResponseHandlerInput = {
    response: Response;
    options: RequestInit;
    url?: string;
    expectedStatus?: number;
};

interface CustomWindow extends Window {
    __DEBUG_FORCE_INTERACTION_REQUIRED?: boolean;
}
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
export const DEBUG_FORCE_INTERACTION_REQUIRED = () =>
    import.meta.env.VITE_ENVIRONMENT === "dev" &&
    (window as CustomWindow).__DEBUG_FORCE_INTERACTION_REQUIRED === true;
// Simple health check cache
let healthCheckCache = {
    isHealthy: true,
    timestamp: 0,
};

const CACHE_DURATION = 30000; // 30 seconds cache for health check results (should be enough until the user logs in to Zscaler)
export const FORBIDDEN_KEY = "isForbidden";

const setForbiddenState = (isForbidden = true): void => {
    localStorage.setItem(FORBIDDEN_KEY, isForbidden.toString());
};

const createHeaders = (token?: string) => {
    const transactionId = uuidv4();

    Sentry.withScope((scope) => {
        scope.setTag("transaction_id", transactionId);
    });
    return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Transaction-ID": transactionId,
    };
};

const isForbiddenGetRequest = ({
    response,
    options,
    endpoint,
}: isForbiddenGetRequestInput): boolean =>
    response.status === 403 && options.method === "GET" && endpoint.includes("/v1/user");

export const handleResponse = async ({
    response,
    options,
    url,
    expectedStatus,
}: ResponseHandlerInput): Promise<Response> => {
    const endpoint = url ? new URL(url).pathname : "";

    if (!response.ok) {
        if (isForbiddenGetRequest({ response, options, endpoint })) {
            setForbiddenState(true);
        }

        throw new Error(`Request to ${endpoint} failed with status ${response.status}`);
    }

    if (expectedStatus !== undefined && response.status !== expectedStatus) {
        throw new Error(`Request to ${endpoint} returned unexpected status ${response.status}`);
    }

    return response;
};

export const postAPI = async ({
    url,
    payload,
    instance,
    expectedStatus,
}: Omit<FetchParams, "method">): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method: "POST",
            body: JSON.stringify(payload),
        };

        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

/**
 * Acquires an authentication token silently and handles interaction errors by redirecting to login.
 *
 * @param instance The MSAL instance to use for token acquisition
 * @returns The acquired token
 * @throws Error if token acquisition fails
 */
type AcquireAuthTokenOptions = {
    forceRefresh?: boolean;
};

export const acquireAuthToken = async (
    instance: IPublicClientApplication,
    options: AcquireAuthTokenOptions = {},
): Promise<string> => {
    try {
        if (DEBUG_FORCE_INTERACTION_REQUIRED()) {
            throw new InteractionRequiredAuthError();
        }

        // Get the active account or first available account to avoid repeated token requests
        const account = instance.getActiveAccount() || instance.getAllAccounts()[0];

        // Use proper scopes to enable MSAL caching - empty scopes bypass the cache
        const silentRequest: SilentRequest = {
            scopes: scopesNeeded, // Required scopes for proper caching
            forceRefresh: options.forceRefresh ?? false,
            ...(account && { account }), // Include account if available to use cached tokens
        };

        const tokenResponse = await instance.acquireTokenSilent(silentRequest);

        const token = tokenResponse.idToken || tokenResponse.accessToken;
        if (!token) {
            throw new Error("No token returned from MSAL");
        }

        return token;
    } catch (error) {
        // Suppress BrowserAuthError noise for interaction_in_progress
        if (
            error instanceof BrowserAuthError &&
            error.message.includes("interaction_in_progress")
        ) {
            throw new Error("Authentication in progress, please wait");
        }

        if (error instanceof InteractionRequiredAuthError || error instanceof AuthError) {
            // Use popup auth when in iframe (widget/copilot mode), redirect otherwise
            const isInIframe = window.self !== window.top;
            if (isInIframe) {
                instance.loginPopup(loginRequest());
            } else {
                instance.loginRedirect(loginRequest());
            }
        }

        throw error;
    }
};

export const getAPI = async ({
    url,
    instance,
    expectedStatus,
}: Omit<FetchParams, "method" | "payload">): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method: "GET",
        };
        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

export const deleteAPI = async ({
    url,
    instance,
    expectedStatus,
}: Omit<FetchParams, "method" | "payload">): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method: "DELETE",
        };

        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

export const fetchWithAuth = async ({
    url,
    method,
    payload,
    instance,
    expectedStatus,
}: FetchParams): Promise<Response> => {
    const execute = async (forceRefresh = false): Promise<Response> => {
        const token = await acquireAuthToken(instance, { forceRefresh });

        const isHealthy = await checkApiHealth(token);
        if (!isHealthy) {
            if (!forceRefresh) {
                return execute(true);
            }
            throw new Error(
                "API is unreachable. Please check your network connection and ensure Zscaler is enabled.",
            );
        }

        const headers = createHeaders(token);
        const options = {
            headers,
            method,
            body: payload ? JSON.stringify(payload) : undefined,
        };

        const response = await fetch(url, options);
        if (response.status === 401 && !forceRefresh) {
            return execute(true);
        }

        return handleResponse({ response, options, url, expectedStatus });
    };

    return execute();
};

// Quick health check function
export const checkApiHealth = async (token?: string): Promise<boolean> => {
    // If we've detected a healthy state, trust it for the whole session
    if (healthCheckCache.isHealthy) {
        return true;
    }

    // For unhealthy state, check cache duration
    if (Date.now() - healthCheckCache.timestamp < CACHE_DURATION) {
        return false;
    }

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(BACKEND_URL, {
            method: "HEAD",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
        });

        clearTimeout(timeoutId);
        const isHealthy = response.ok;

        // Update cache
        healthCheckCache = {
            isHealthy,
            timestamp: Date.now(),
        };

        return isHealthy;
    } catch (error) {
        // Capture detailed error information for debugging
        Sentry.withScope((scope) => {
            scope.setTransactionName("checkApiHealth_internal");
            scope.setTag("error_type", "api_health_check_internal");
            scope.setExtra("backend_url", BACKEND_URL);
            scope.setExtra("error_name", error instanceof Error ? error.name : "Unknown");
            scope.setExtra("error_message", error instanceof Error ? error.message : String(error));
            scope.setExtra("has_token", !!token);
            scope.setExtra("cache_was_healthy", healthCheckCache.isHealthy);
            scope.setExtra("cache_age_ms", Date.now() - healthCheckCache.timestamp);

            // Don't capture AbortError (timeout) as critical - these are expected
            const level =
                error instanceof Error && error.name === "AbortError" ? "warning" : "error";

            Sentry.captureException(error, {
                level,
                tags: {
                    component: "checkApiHealth",
                    error_source:
                        error instanceof Error && error.name === "AbortError"
                            ? "timeout"
                            : "network_or_auth",
                },
            });
        });

        // Update cache with unhealthy state
        healthCheckCache = {
            isHealthy: false,
            timestamp: Date.now(),
        };
        return false;
    }
};
